import React, { useState, useEffect, memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import ProfileSection from "./common/ProfileSection";
import NavigationItem from "./common/NavigationItem";
import ProjectsMenu from "./common/ProjectsMenu";

// Social Media Icons Component
const SocialMediaIcons = memo(({ icons, isMini = false }) => {
  return (
    <ul className="list-none flex space-x-4">
      {icons.map((icon, index) => (
        <li key={index} className="transform hover:scale-110 transition-transform duration-200">
          <a 
            href={icon.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={icon.img}
              alt={icon.alt}
              className={`${isMini ? "w-[15px] h-[15px]" : "w-[30px] h-[30px]"} filter hover:brightness-110`}
            />
          </a>
        </li>
      ))}
    </ul>
  );
});

SocialMediaIcons.propTypes = {
  icons: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      img: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ).isRequired,
  isMini: PropTypes.bool,
};

const HomePageHeaderView = ({ navLinks, socialMediaIcons, onClickSetCurrentProject, profile }) => {
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const navigate = useNavigate();

  const handleClickOutside = useCallback((event) => {
    const menuElement = document.getElementById("projects-menu");
    const target = event.target;
    
    const isClickInsideMenu = menuElement && menuElement.contains(target);
    const isClickOnProjectsNav = target.closest('li')?.querySelector('span')?.textContent === 'Projects';
    
    if (menuElement && !isClickInsideMenu && !isClickOnProjectsNav) {
      setIsProjectsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  const handleNavigation = useCallback((link) => {
    if (link.name === "Download CV") {
      window.open(link.link, '_blank');
    } else if (link.name === "Projects") {
      setIsProjectsOpen(prev => !prev);
    } else {
      navigate(link.link);
    }
  }, [navigate]);

  const renderNavOptions = useCallback(() => {
    return navLinks.map((link, index) => (
      <NavigationItem
        key={index}
        name={link.name}
        onClick={() => handleNavigation(link)}
      />
    ));
  }, [navLinks, handleNavigation]);

  return (
    <div className="flex justify-center w-full max-w-[360px] sm:max-w-[700px] xl:max-w-screen-2xl bg-gradient-to-br from-primary-50 to-primary-100 items-center xl:flex-row sm:flex-col mx-auto py-8 sm:py-10 xl:py-12 ring-1 ring-primary-200 shadow-lg rounded-lg transform hover:shadow-xl transition-all duration-300">
      <ProfileSection profile={profile} socialMediaIcons={socialMediaIcons} />
      
      <div className="w-full max-w-[360px] sm:max-w-[700px] sm:h-[400px] flex flex-col justify-center">
        <nav className="w-full flex flex-col items-center justify-center mt-[15px] sm:mt-[60px]">
          <ul className="list-none grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 w-full h-full shadow-lg rounded-md overflow-hidden gap-2 px-2 py-2 bg-white/70 backdrop-blur-sm">
            {renderNavOptions()}
          </ul>
          <ProjectsMenu
            isOpen={isProjectsOpen}
            projects={navLinks.find(link => link.name === "Projects")?.projects || []}
            onProjectSelect={onClickSetCurrentProject}
            menuId="projects-menu"
          />
        </nav>

        <div className="flex py-4 sm:hidden animate-slide-up">
          <ProfileSection profile={profile} socialMediaIcons={socialMediaIcons} isMobile={true} />
          <div>
            <h2 className="mb-[2px] mt-[2px] px-2 font-bold text-sm text-primary-900">
              {profile.role}
            </h2>
            <div className="text-justify text-[13px] px-2 pr-4 pt-0 text-secondary-700">
              {typeof profile.intro === 'function' ? profile.intro() : profile.intro}
            </div>
          </div>
        </div>

        <div className="hidden sm:block animate-fade-in">
          <div className="w-full px-8 py-4 mt-8">
            <p className="text-secondary-700 text-justify break-words">
              <span className="font-bold text-primary-900 text-[1.15em] mr-1">Game Developer </span>
              at Filimundus with expertise in both Unity and C++ development. Skilled in engine development, systems programming, and application modernization. Currently focused on updating and expanding Unity-based projects. Experience in building robust backend systems with AWS and Firebase, and creating engaging player experiences across different technologies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

HomePageHeaderView.propTypes = {
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      link: PropTypes.string,
      projects: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          title: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  socialMediaIcons: PropTypes.array.isRequired,
  onClickSetCurrentProject: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    img: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    intro: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  }).isRequired,
};

export default memo(HomePageHeaderView);
